import { useState, useEffect, useRef, useCallback } from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import { operationAPI, authAPI, adminAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'

// Vibrant colorful palette for reference number-based coloring
// Text colors are darker for better contrast with light backgrounds
const vibrantColors = [
  { bg: '#E6F7F9', border: '#0a5fe0', text: '#0C5D73' }, // Red
  { bg: '#FEF3C7', border: '#F59E0B', text: '#92400E' }, // Amber
  { bg: '#DBEAFE', border: '#3B82F6', text: '#1E40AF' }, // Blue
  { bg: '#E9D5FF', border: '#A855F7', text: '#6B21A8' }, // Purple
  { bg: '#FCE7F3', border: '#EC4899', text: '#9F1239' }, // Pink
  { bg: '#D1FAE5', border: '#10B981', text: '#047857' }, // Emerald
  { bg: '#FED7AA', border: '#F97316', text: '#C2410C' }, // Orange
  { bg: '#FDE68A', border: '#EAB308', text: '#854D0E' }, // Yellow
  { bg: '#BFDBFE', border: '#60A5FA', text: '#1E3A8A' }, // Light Blue
  { bg: '#DDD6FE', border: '#A78BFA', text: '#5B21B6' }, // Light Purple
  { bg: '#FBCFE8', border: '#F472B6', text: '#9F1239' }, // Light Pink
  { bg: '#A7F3D0', border: '#34D399', text: '#065F46' }, // Light Green
  { bg: '#FCD34D', border: '#FBBF24', text: '#78350F' }, // Golden
  { bg: '#C7D2FE', border: '#818CF8', text: '#3730A3' }, // Indigo
  { bg: '#F9A8D4', border: '#F472B6', text: '#9F1239' }, // Rose
  { bg: '#86EFAC', border: '#4ADE80', text: '#166534' }, // Green
  { bg: '#FBBF24', border: '#F59E0B', text: '#78350F' }, // Amber Dark
  { bg: '#93C5FD', border: '#60A5FA', text: '#1E40AF' }, // Sky Blue
  { bg: '#C4B5FD', border: '#A78BFA', text: '#5B21B6' }, // Violet
  { bg: '#FCA5A5', border: '#F87171', text: '#991B1B' }, // Red
  { bg: '#6EE7B7', border: '#34D399', text: '#047857' }, // Teal Green
  { bg: '#FCD34D', border: '#FBBF24', text: '#78350F' }, // Yellow Gold
  { bg: '#A5B4FC', border: '#818CF8', text: '#3730A3' }, // Indigo Light
  { bg: '#F9A8D4', border: '#F472B6', text: '#9F1239' }, // Fuchsia
  { bg: '#7DD3FC', border: '#38BDF8', text: '#0C4A6E' }, // Cyan
  { bg: '#B9F5D0', border: '#6EE7B7', text: '#065F46' }, // Mint
  { bg: '#FED7AA', border: '#FDBA74', text: '#9A3412' }, // Peach
  { bg: '#E0E7FF', border: '#A5B4FC', text: '#4338CA' }, // Lavender
  { bg: '#FECACA', border: '#FCA5A5', text: '#991B1B' }, // Coral
  { bg: '#BEF264', border: '#A3E635', text: '#365314' }, // Lime
]

// Generate a consistent color for a reference number
const getReferenceColor = (referenceNumber) => {
  if (!referenceNumber || referenceNumber.trim() === '') {
    // Default color for operations without reference number
    return vibrantColors[0] // Default to teal
  }

  // Normalize reference number (case-insensitive, trimmed)
  const normalized = referenceNumber.trim().toUpperCase()
  
  // Simple hash function to convert reference number to a number
  let hash = 0
  for (let i = 0; i < normalized.length; i++) {
    const char = normalized.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32-bit integer
  }
  
  // Use absolute value and modulo to get index
  const index = Math.abs(hash) % vibrantColors.length
  return vibrantColors[index]
}

// Parse operation description to extract structured data
const parseDescription = (description) => {
  if (!description) return {}
  
  const lines = description.split('\n')
  const data = {
    client: '',
    port: '',
    country: '',
    surveyor: '',
    notes: '',
  }
  
  lines.forEach(line => {
    if (line.startsWith('Client:')) {
      data.client = line.replace('Client:', '').trim()
    } else if (line.startsWith('Port:')) {
      const portLine = line.replace('Port:', '').trim()
      const parts = portLine.split(',')
      data.port = parts[0]?.trim() || ''
      data.country = parts[1]?.trim() || ''
    } else if (line.startsWith('Surveyor:')) {
      data.surveyor = line.replace('Surveyor:', '').trim()
    } else if (line.startsWith('Notes:')) {
      data.notes = line.replace('Notes:', '').trim()
    } else if (line && !line.startsWith('Client:') && !line.startsWith('Port:') && !line.startsWith('Surveyor:')) {
      // Additional notes lines
      if (data.notes) {
        data.notes += ' ' + line.trim()
      }
    }
  })
  
  return data
}

// Operation Detail Modal Component
const OperationModal = ({ operation, isOpen, onClose, isReadOnlyClient }) => {
  
  if (!isOpen || !operation) return null

  const parsed = parseDescription(operation.extendedProps?.description || '')
  // Use reference number-based color for consistent coloring
  const refColor = getReferenceColor(operation.extendedProps?.survey_ref || '')
  const bgColor = refColor.bg
  const borderColor = refColor.border

  // Format date for display (timezone-safe - matches FullCalendar Europe/London timezone)
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A'
    
    // If it's already a string in YYYY-MM-DD format, parse components directly
    if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-').map(Number)
      // Create date in UTC to avoid timezone shifts, then format
      const date = new Date(Date.UTC(year, month - 1, day))
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        timeZone: 'UTC' // Use UTC to avoid timezone shifts
      })
    }
    
    // For other formats, create date object and format
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      timeZone: 'UTC' // Use UTC to avoid timezone shifts
    })
  }

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col border-l-4"
        style={{ borderLeftColor: borderColor }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div 
          className="p-6 rounded-t-3xl"
          style={{ 
            backgroundColor: bgColor,
          }}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-2 text-jnj-gray-800">{operation.title}</h2>
              <div className="flex items-center gap-4 text-sm text-jnj-gray-700">
                <span className="flex items-center gap-1.5 bg-white/60 px-3 py-1.5 rounded-full">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {formatDate(operation.start)}
                </span>
                {operation.end && operation.end !== operation.start && (
                  <span className="flex items-center gap-1.5 bg-white/60 px-3 py-1.5 rounded-full">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {formatDate(operation.end)}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-jnj-gray-600 hover:text-jnj-gray-800 hover:bg-white/60 transition-all p-2 rounded-full"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Survey Reference */}
          {operation.extendedProps?.survey_ref && (
            <div className="flex items-center gap-3 p-4 rounded-xl border-l-4" style={{ 
              backgroundColor: `${bgColor}80`,
              borderLeftColor: borderColor 
            }}>
              <div className="flex-shrink-0">
                <svg className="w-5 h-5" style={{ color: borderColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-jnj-gray-600 font-medium">Survey Reference</p>
                <p className="text-lg font-semibold text-jnj-gray-800">{operation.extendedProps.survey_ref}</p>
              </div>
            </div>
          )}

          {/* Client Information */}
          {parsed.client && (
            <div className="border-l-4 pl-4 py-2 rounded-r-lg" style={{ 
              borderLeftColor: borderColor,
              backgroundColor: `${bgColor}40`
            }}>
              <p className="text-sm text-jnj-gray-600 font-medium mb-1">Client</p>
              <p className="text-lg text-jnj-gray-800">{parsed.client}</p>
            </div>
          )}

          {/* Location Information */}
          {(parsed.port || parsed.country) && (
            <div className="border-l-4 pl-4 py-2 rounded-r-lg" style={{ 
              borderLeftColor: borderColor,
              backgroundColor: `${bgColor}40`
            }}>
              <p className="text-sm text-jnj-gray-600 font-medium mb-1">Location</p>
              <p className="text-lg text-jnj-gray-800">
                {parsed.port}
                {parsed.country && `, ${parsed.country}`}
              </p>
            </div>
          )}

          {/* Surveyor Information - Hide for ReadOnlyClient users */}
          {!isReadOnlyClient && parsed.surveyor && (
            <div className="border-l-4 pl-4 py-2 rounded-r-lg" style={{ 
              borderLeftColor: borderColor,
              backgroundColor: `${bgColor}40`
            }}>
              <p className="text-sm text-jnj-gray-600 font-medium mb-1">Surveyor</p>
              <p className="text-lg text-jnj-gray-800">{parsed.surveyor}</p>
            </div>
          )}

          {/* Notes - Hide for ReadOnlyClient users */}
          {!isReadOnlyClient && parsed.notes && (
            <div className="rounded-xl p-4" style={{ backgroundColor: `${bgColor}60` }}>
              <p className="text-sm text-jnj-gray-600 font-medium mb-2">Notes</p>
              <p className="text-jnj-gray-800 whitespace-pre-wrap leading-relaxed">{parsed.notes}</p>
            </div>
          )}

          {/* Survey Type */}
          {operation.extendedProps?.survey_type && (
            <div className="flex items-center gap-2">
              <span 
                className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold text-jnj-gray-700"
                style={{ backgroundColor: `${bgColor}80` }}
              >
                {operation.extendedProps.survey_type}
              </span>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="border-t border-jnj-gray-200 p-6 flex justify-end bg-jnj-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-xl font-semibold transition-all duration-200 hover:scale-105 active:scale-95"
            style={{ 
              backgroundColor: borderColor,
              color: 'white'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

// Custom Event Content Renderer with Neon Shine Effects
// Note: This component is used as a render function by FullCalendar
// We'll create a wrapper component that can use hooks properly
const EventContentWrapper = ({ event, isReadOnlyClient }) => {
  
  // Get reference number from extendedProps for consistent coloring
  const surveyRef = event.extendedProps?.survey_ref || ''
  const refColor = getReferenceColor(surveyRef)
  const bgColor = refColor.bg
  const borderColor = refColor.border
  const textColor = refColor.text
  
  // Parse description to extract information
  const description = event.extendedProps?.description || ''
  const parsed = parseDescription(description)
  
  // Extract vessel name from title (before the dash)
  const titleParts = event.title?.split('-') || []
  const fullVesselName = titleParts[0]?.trim() || event.title
  
  // Format vessel name (truncate to 12 characters)
  const truncateText = (text, maxLength) => {
    if (!text) return ''
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '..'
  }
  const displayVesselName = truncateText(fullVesselName, 12)
  
  // Format reference number (show only last 4 characters)
  const formattedRef = surveyRef ? '#' + surveyRef.slice(-4) : ''
  
  // Extract port name (before the comma if exists)
  const portName = parsed.port ? parsed.port.split(',')[0].trim() : ''
  
  // Format surveyor code (max 6 characters) - Hide for clientusers
  const formattedSurveyor = parsed.surveyor ? parsed.surveyor.substring(0, 6) : ''

  return (
    <div 
      className="fc-event-main-frame event-card-neon relative overflow-hidden rounded-lg my-1"
      style={{ 
        borderLeftColor: borderColor,
        borderLeftWidth: '4px',
        backgroundColor: bgColor,
        border: 'none',
        borderTopLeftRadius: '0.5rem',
        borderBottomLeftRadius: '0.5rem',
        boxShadow: `0 4px 12px -2px ${borderColor}50, 0 2px 8px -2px ${borderColor}30, inset 0 1px 0 0 rgba(255,255,255,0.1)`,
        position: 'relative',
      }}
    >
      {/* Enhanced glow effect on left border */}
      <div 
        className="absolute left-0 top-0 bottom-0 w-1 opacity-70 blur-sm neon-glow rounded-full"
        style={{ 
          backgroundColor: borderColor,
          boxShadow: `0 0 6px ${borderColor}80, 0 0 12px ${borderColor}50, 0 0 18px ${borderColor}30`,
        }}
      />
      
      {/* Subtle gradient overlay for depth */}
      <div 
        className="absolute inset-0 opacity-30 rounded-lg"
        style={{
          background: `linear-gradient(135deg, ${borderColor}20 0%, transparent 50%)`,
          pointerEvents: 'none'
        }}
      />
      
      {/* Content - 2x2 grid layout matching backend */}
      <div className="relative z-10 px-2.5 py-0.5 w-full h-full flex flex-col justify-center">
        <div className="event-content w-full">
          {/* Row 1: Port (left) and Reference (right) */}
          <div className="event-row">
            <span className="event-port" style={{ color: textColor }}>{portName || ''}</span>
            <span className="event-ref" style={{ color: textColor }}>{formattedRef}</span>
          </div>
          {/* Row 2: Surveyor (left) and Vessel (right) - Hide surveyor for ReadOnlyClient users */}
          <div className="event-row">
            {isReadOnlyClient ? (
              <span className="event-vessel" style={{ width: '100%', color: textColor }}>{displayVesselName}</span>
            ) : (
              <>
                <span className="event-surveyor" style={{ color: textColor }}>{formattedSurveyor}</span>
                <span className="event-vessel" style={{ color: textColor }}>{displayVesselName}</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// Wrapper to create EventContent
const createEventContent = (isReadOnlyClient) => {
  return ({ event }) => <EventContentWrapper event={event} isReadOnlyClient={isReadOnlyClient} />
}

const OperationCalendar = () => {
  const { user, isSuperuser, userGroups, isFinanceManager } = useAuth()
  // Direct group-based and user-based access control
  const isAdmin = isSuperuser || userGroups.includes('Admin')
  const hasFullAccess = isSuperuser || isAdmin || isFinanceManager
  
  // Access control check - Only Superuser, Admin, Manager, Finance Manager, Staff, ReadOnlyClient, or ReadOnlySurveyor can access calendar
  useEffect(() => {
    const allowedGroups = ['Admin', 'Manager', 'Finance Manager', 'Staff', 'ReadOnlyClient', 'ReadOnlySurveyor']
    const hasAccess = isSuperuser || userGroups.some(group => allowedGroups.includes(group))

    if (!hasAccess) {
      window.location.href = '/'
      return
    }
  }, [isSuperuser, userGroups])
  const [events, setEvents] = useState([])
  const [allEvents, setAllEvents] = useState([]) // Store all events for filtering
  const [loading, setLoading] = useState(true)
  const [selectedOperation, setSelectedOperation] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const isDraggingRef = useRef(false)
  const observerRef = useRef(null)
  const calendarRef = useRef(null)
  const [selectedSurveyors, setSelectedSurveyors] = useState([])
  const [selectedCountries, setSelectedCountries] = useState([])
  const [selectedPorts, setSelectedPorts] = useState([])
  const [availableSurveyors, setAvailableSurveyors] = useState([])
  const [availableCountries, setAvailableCountries] = useState([])
  const [availablePorts, setAvailablePorts] = useState([])
  const [activeFilterField, setActiveFilterField] = useState('')
  const [showFilter, setShowFilter] = useState(false)
  // ReadOnly group users have read-only access, others can edit
  const isReadOnlyClient = userGroups.includes('ReadOnlyClient')
  const isReadOnlySurveyor = userGroups.includes('ReadOnlySurveyor')
  const isReadOnly = !hasFullAccess && (isReadOnlyClient || isReadOnlySurveyor) && !userGroups.some(g => ['Admin', 'Manager', 'Finance Manager', 'Staff'].includes(g))

  const fetchOperations = useCallback(async () => {
    try {
      // Admin users get all operations via admin endpoint, others get filtered via regular endpoint
      const useAdminEndpoint = hasFullAccess
      const response = useAdminEndpoint 
        ? await adminAPI.getOperations()
        : await operationAPI.list()
      
      // Handle different response structures
      const operationsData = hasFullAccess 
        ? (response.data.data || response.data || [])
        : (response.data || [])
      
      const operations = operationsData.map(op => {
        // Parse description to extract reference number
        const parsed = parseDescription(op.description || '')
        const surveyRef = op.survey_ref || ''
        
        // Get reference number-based color for consistent coloring
        const refColor = getReferenceColor(surveyRef)
        
        return {
          id: op.id.toString(),
          title: op.title,
          start: op.start_date, // Keep as string for date-only handling
          end: op.end_date || null, // Keep as string for date-only handling
          allDay: true, // Mark as all-day events
          backgroundColor: refColor.bg,
          borderColor: 'transparent', // We handle border in custom renderer
          extendedProps: {
            description: op.description,
            survey_ref: op.survey_ref,
            survey_type: op.survey_type,
            country: parsed.country, // Keep country for reference
            color: op.color, // Keep original color for reference
          },
        }
      })
      setEvents(operations)
      setAllEvents(operations) // Store all events for filtering
      
      // Populate filter options from raw API data (before transformation)
      populateFilterOptions(operationsData)
    } catch (error) {
      console.error('Error fetching operations:', error)
      console.error('Error details:', error.response?.data || error.message)
      
      // Check if it's an authentication error
      if (error.response?.status === 403 || error.response?.status === 401) {
        console.warn('Authentication error - user may need to log in')
        // Don't show operations, let the empty state handle it
        return
      }
    } finally {
      setLoading(false)
    }
  }, [hasFullAccess])

  useEffect(() => {
    fetchOperations()
  }, [fetchOperations])


  // Populate filter dropdown options from operations data
  const populateFilterOptions = (operationsData) => {
    const surveyors = new Set()
    const countries = new Set()
    const ports = new Set()
    
    operationsData.forEach(op => {
      // Get description from the raw operation data
      const description = op.description || ''
      const parsed = parseDescription(description)
      
      // Extract surveyors
      if (parsed.surveyor) {
        parsed.surveyor.split(',').forEach(s => {
          const trimmed = s.trim()
          if (trimmed && trimmed !== 'Not assigned') {
            surveyors.add(trimmed)
          }
        })
      }
      
      // Extract country and port
      if (parsed.country) {
        countries.add(parsed.country)
      }
      if (parsed.port) {
        ports.add(parsed.port)
      }
    })
    
    const surveyorsArray = Array.from(surveyors).sort()
    const countriesArray = Array.from(countries).sort()
    const portsArray = Array.from(ports).sort()
    
    setAvailableSurveyors(surveyorsArray)
    setAvailableCountries(countriesArray)
    setAvailablePorts(portsArray)
  }

  // Filter events based on selected criteria
  const applyFilters = () => {
    if (selectedSurveyors.length === 0 && selectedCountries.length === 0 && selectedPorts.length === 0) {
      // No filters - show all events
      setEvents(allEvents)
      return
    }
    
    const filtered = allEvents.filter(event => {
      const parsed = parseDescription(event.extendedProps?.description || '')
      
      // Check surveyor match - if no surveyors selected, don't filter by surveyor
      let matchesSurveyor = selectedSurveyors.length === 0
      if (selectedSurveyors.length > 0 && parsed.surveyor) {
        const eventSurveyors = parsed.surveyor.split(',').map(s => s.trim())
        matchesSurveyor = selectedSurveyors.some(selected => 
          eventSurveyors.includes(selected)
        )
      }
      
      // Check country match - if no countries selected, don't filter by country
      let matchesCountry = selectedCountries.length === 0
      if (selectedCountries.length > 0 && parsed.country) {
        matchesCountry = selectedCountries.includes(parsed.country)
      }
      
      // Check port match - if no ports selected, don't filter by port
      let matchesPort = selectedPorts.length === 0
      if (selectedPorts.length > 0 && parsed.port) {
        matchesPort = selectedPorts.includes(parsed.port)
      }
      
      return matchesSurveyor && matchesCountry && matchesPort
    })
    
    setEvents(filtered)
  }

  // Reset all filters
  const resetFilters = () => {
    setSelectedSurveyors([])
    setSelectedCountries([])
    setSelectedPorts([])
    setActiveFilterField('')
    setEvents(allEvents)
  }

  // Handle field selection change
  const handleFieldChange = (field) => {
    setActiveFilterField(field === activeFilterField ? '' : field)
  }

  // Handle option toggle for multi-select
  const toggleOption = (field, option) => {
    if (field === 'surveyor') {
      setSelectedSurveyors(prev => 
        prev.includes(option) 
          ? prev.filter(s => s !== option)
          : [...prev, option]
      )
    } else if (field === 'country') {
      setSelectedCountries(prev => 
        prev.includes(option) 
          ? prev.filter(c => c !== option)
          : [...prev, option]
      )
    } else if (field === 'port') {
      setSelectedPorts(prev => 
        prev.includes(option) 
          ? prev.filter(p => p !== option)
          : [...prev, option]
      )
    }
  }

  const handleEventClick = (info) => {
    setSelectedOperation(info.event)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setSelectedOperation(null)
    setIsModalOpen(false)
  }

  // Handle drag start - set custom drag image with correct offset for accurate cursor tracking
  const handleEventDragStart = (info) => {
    isDraggingRef.current = true
    const el = info.el
    const jsEvent = info.jsEvent
    
    if (el && jsEvent) {
      // Aggressively prevent FullCalendar from hiding the element
      // Use !important via setProperty to override any FullCalendar styles
      el.style.setProperty('opacity', '0.6', 'important')
      el.style.setProperty('visibility', 'visible', 'important')
      el.style.setProperty('display', 'block', 'important')
      el.style.setProperty('pointer-events', 'none', 'important')
      
      // Reset any hover effects that might interfere with drag positioning
      // Remove ALL transitions and animations immediately
      el.style.transform = 'none'
      el.style.transition = 'none'
      el.style.animation = 'none'
      
      // Also disable transitions on all child elements
      const children = el.querySelectorAll('*')
      children.forEach(child => {
        child.style.transition = 'none'
        child.style.animation = 'none'
      })
      
      // Calculate the offset from mouse click position to element's top-left corner
      // This ensures the drag preview follows the cursor exactly (1:1 movement)
      const rect = el.getBoundingClientRect()
      const offsetX = jsEvent.clientX - rect.left
      const offsetY = jsEvent.clientY - rect.top
      
      // Create a clone of the element to use as drag image
      // This ensures the drag preview is visible and matches the original element
      const dragImage = el.cloneNode(true)
      dragImage.style.position = 'absolute'
      dragImage.style.top = '-9999px'
      dragImage.style.left = '-9999px'
      dragImage.style.opacity = '0.95'
      dragImage.style.pointerEvents = 'none'
      dragImage.style.zIndex = '9999'
      dragImage.style.margin = '0'
      dragImage.style.padding = '0'
      // Ensure the clone has the same computed dimensions
      const computedStyle = window.getComputedStyle(el)
      dragImage.style.width = computedStyle.width
      dragImage.style.height = computedStyle.height
      dragImage.style.boxSizing = 'border-box'
      // Force visibility
      dragImage.style.visibility = 'visible'
      dragImage.style.display = 'block'
      document.body.appendChild(dragImage)
      
      // Force a reflow to ensure the clone is rendered before using it as drag image
      void dragImage.offsetHeight
      
      // Set custom drag image with calculated offset to fix cursor trajectory mismatch
      const nativeDragEvent = jsEvent.originalEvent || jsEvent
      if (nativeDragEvent && nativeDragEvent.dataTransfer) {
        try {
          // Use the cloned element as drag image with correct offset
          // This makes the ghost element follow the cursor exactly
          nativeDragEvent.dataTransfer.setDragImage(dragImage, offsetX, offsetY)
          
          // Clean up the clone after drag starts (browser takes ownership)
          setTimeout(() => {
            if (document.body.contains(dragImage)) {
              document.body.removeChild(dragImage)
            }
          }, 0)
        } catch (e) {
          // If setDragImage fails, clean up and fall back to default
          if (document.body.contains(dragImage)) {
            document.body.removeChild(dragImage)
          }
          console.debug('Could not set drag image in handleEventDragStart, using default')
        }
      } else {
        // Clean up if dataTransfer is not available
        if (document.body.contains(dragImage)) {
          document.body.removeChild(dragImage)
        }
      }
      
      // Use MutationObserver to prevent FullCalendar from hiding the element
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
      
      observerRef.current = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
            // If opacity is being set to 0 or visibility hidden, override it
            const currentOpacity = el.style.opacity || window.getComputedStyle(el).opacity
            const currentVisibility = el.style.visibility || window.getComputedStyle(el).visibility
            const currentDisplay = el.style.display || window.getComputedStyle(el).display
            
            if (currentOpacity === '0' || currentVisibility === 'hidden' || currentDisplay === 'none') {
              el.style.setProperty('opacity', '0.6', 'important')
              el.style.setProperty('visibility', 'visible', 'important')
              el.style.setProperty('display', 'block', 'important')
            }
          }
        })
      })
      
      observerRef.current.observe(el, {
        attributes: true,
        attributeFilter: ['style', 'class'],
        childList: false,
        subtree: false
      })
    }
  }

  // Handle drag end - restore transitions and visibility after drag completes
  const handleEventDragStop = (info) => {
    isDraggingRef.current = false
    
    // Clean up observer
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }
    
    const el = info.el
    if (el) {
      // Remove all the important styles we added
      el.style.removeProperty('opacity')
      el.style.removeProperty('visibility')
      el.style.removeProperty('display')
      el.style.removeProperty('pointer-events')
      // Restore transition for smooth hover effects
      el.style.transition = 'opacity 0.2s, box-shadow 0.2s'
    }
  }

  // Handle event mount - ensure proper positioning for drag operations
  const handleEventDidMount = (info) => {
    const el = info.el
    if (el && !isReadOnly) {
      // Ensure the event element is ready for drag operations
      // Set initial transition for hover effects
      el.style.transition = 'opacity 0.2s, box-shadow 0.2s'
      // Ensure no transforms that could interfere with position calculation
      el.style.transform = 'none'
      
      // Note: The drag image is set in handleEventDragStart which has access to
      // both the element and the click position for accurate offset calculation
    }
  }

  // Format date to YYYY-MM-DD (timezone-safe - matches FullCalendar Europe/London timezone)
  const formatDate = (date) => {
    if (!date) return ''
    
    // If it's already a string in YYYY-MM-DD format, return it as-is
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date
    }
    
    // For Date objects, extract components using UTC to avoid timezone shifts
    const d = date instanceof Date ? date : new Date(date)
    const year = d.getUTCFullYear() // Use UTC
    const month = String(d.getUTCMonth() + 1).padStart(2, '0') // Use UTC
    const day = String(d.getUTCDate()).padStart(2, '0') // Use UTC
    
    return `${year}-${month}-${day}`
  }

  // Handle event drop (when moved to a new date)
  const handleEventDrop = async (info) => {
    if (isReadOnly) {
      info.revert()
      return
    }

    if (!window.confirm('Are you sure you want to move this event?')) {
      info.revert()
      return
    }

    // Store the original position for smooth transition
    const event = info.event
    const originalStart = event.start
    const originalEnd = event.end
    
    try {
      // For all-day events, FullCalendar provides the date in the configured timezone
      // We need to extract it correctly to avoid timezone conversion issues
      let startDate, endDate
      
      // Extract date from event
      // For all-day events, FullCalendar should provide startStr/endStr (timezone-safe YYYY-MM-DD strings)
      // If not available, we extract from the Date object in Europe/London timezone
      
      // For all-day events, FullCalendar provides startStr/endStr which are timezone-safe YYYY-MM-DD strings
      // Always prefer startStr for all-day events to avoid timezone conversion issues
      if (event.allDay && event.startStr && /^\d{4}-\d{2}-\d{2}$/.test(event.startStr)) {
        // Use startStr directly - it's already in the correct format and timezone-safe
        startDate = event.startStr
        endDate = (event.endStr && /^\d{4}-\d{2}-\d{2}$/.test(event.endStr)) ? event.endStr : startDate
      } else if (event.startStr && /^\d{4}-\d{2}-\d{2}$/.test(event.startStr)) {
        // Even if not explicitly allDay, if startStr is available, use it
        startDate = event.startStr
        endDate = (event.endStr && /^\d{4}-\d{2}-\d{2}$/.test(event.endStr)) ? event.endStr : startDate
      } else {
        // Fallback: extract from Date object in Europe/London timezone
        // This should rarely happen for all-day events, but handle it gracefully
        startDate = formatDate(event.start)
        endDate = event.end ? formatDate(event.end) : startDate
      }

      const data = {
        id: parseInt(event.id),
        title: event.title,
        start: startDate,
        end: endDate,
        description: event.extendedProps?.description || '',
        color: event.extendedProps?.color || '#0a5fe0',
        survey_ref: event.extendedProps?.survey_ref || null,
        surveyType: event.extendedProps?.survey_type || '',
        update_eta: true,
      }

      // Ensure we have a CSRF token before saving
      try {
        await authAPI.getCsrfToken()
      } catch (csrfError) {
        console.warn('Could not fetch CSRF token before save:', csrfError)
      }
      
      const response = await operationAPI.save(data)
      
      if (response.data?.status === 'success') {
        // Don't refresh all events - let FullCalendar handle the transition animation
        // The event is already in its new position visually, we just need to ensure
        // the data is synced. Only refresh if there's a mismatch.
        // This allows the smooth transition from initial to final position.
      } else {
        throw new Error(response.data?.message || 'Failed to update event')
      }
    } catch (error) {
      console.error('Error updating event:', error)
      alert('Error updating event: ' + (error.response?.data?.message || error.message))
      // Revert to original position on error
      event.setStart(originalStart)
      if (originalEnd) {
        event.setEnd(originalEnd)
      }
      info.revert()
    }
  }

  // Handle event resize (when duration is changed)
  const handleEventResize = async (info) => {
    if (isReadOnly) {
      info.revert()
      return
    }

    if (!window.confirm('Are you sure you want to change the event duration?')) {
      info.revert()
      return
    }

    // Store the original position for smooth transition
    const event = info.event
    const originalStart = event.start
    const originalEnd = event.end

    try {
      // For all-day events, use startStr/endStr if available (timezone-safe)
      let startDate, endDate
      
      // Always prefer startStr for all-day events to avoid timezone conversion issues
      if (event.allDay && event.startStr && /^\d{4}-\d{2}-\d{2}$/.test(event.startStr)) {
        startDate = event.startStr
        endDate = (event.endStr && /^\d{4}-\d{2}-\d{2}$/.test(event.endStr)) ? event.endStr : startDate
      } else if (event.startStr && /^\d{4}-\d{2}-\d{2}$/.test(event.startStr)) {
        // Even if not explicitly allDay, if startStr is available, use it
        startDate = event.startStr
        endDate = (event.endStr && /^\d{4}-\d{2}-\d{2}$/.test(event.endStr)) ? event.endStr : startDate
      } else {
        // Fallback: format the Date object in Europe/London timezone
        startDate = formatDate(event.start)
        endDate = event.end ? formatDate(event.end) : startDate
      }

      const data = {
        id: parseInt(event.id),
        title: event.title,
        start: startDate,
        end: endDate,
        description: event.extendedProps?.description || '',
        color: event.extendedProps?.color || '#0a5fe0',
        survey_ref: event.extendedProps?.survey_ref || null,
        surveyType: event.extendedProps?.survey_type || '',
        update_eta: true,
      }

      // Ensure we have a CSRF token before saving
      try {
        await authAPI.getCsrfToken()
      } catch (csrfError) {
        console.warn('Could not fetch CSRF token before save:', csrfError)
      }
      
      const response = await operationAPI.save(data)
      
      if (response.data?.status === 'success') {
        // Don't refresh all events - let FullCalendar handle the transition animation
        // The event is already in its new position visually, we just need to ensure
        // the data is synced. This allows the smooth transition from initial to final position.
      } else {
        throw new Error(response.data?.message || 'Failed to update event')
      }
    } catch (error) {
      console.error('Error updating event:', error)
      alert('Error updating event: ' + (error.response?.data?.message || error.message))
      // Revert to original position on error
      event.setStart(originalStart)
      if (originalEnd) {
        event.setEnd(originalEnd)
      }
      info.revert()
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jnj-primary"></div>
      </div>
    )
  }

  return (
    <div className="page-container">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold"><span className="text-premium">JNJ Operations Calendar</span></h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">Schedule and track survey operations by date</p>
        </div>
        <button
          type="button"
          onClick={() => setShowFilter(!showFilter)}
          className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
            showFilter
              ? 'bg-premium text-white shadow-[0_2px_8px_rgba(1,109,247,0.3)]'
              : 'border border-[#d9e2f1] bg-white text-[#0a5fe0] hover:border-[#0a5fe0] hover:bg-[#f4f7fc]'
          }`}
          title="Toggle Filters"
          aria-expanded={showFilter}
        >
          <i className="bi bi-funnel"></i>
          Filters
          {selectedSurveyors.length + selectedCountries.length + selectedPorts.length > 0 && (
            <span className={`ml-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold ${showFilter ? 'bg-white text-[#0029a4]' : 'bg-[#0a5fe0] text-white'}`}>
              {selectedSurveyors.length + selectedCountries.length + selectedPorts.length}
            </span>
          )}
        </button>
      </div>

      {/* Filter Controls - Similar to Dashboard */}
      {showFilter && (
      <div className="bg-white rounded-xl border border-[#d9e2f1] shadow-[0_1px_6px_rgba(1,109,247,0.12)] overflow-hidden mb-6">
        <div className="table-head-premium px-5 py-2">
          <h3 className="text-sm font-semibold text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filter Options
            </div>
            <button
              onClick={() => setShowFilter(false)}
              className="p-1 rounded-md hover:bg-white/20 transition-colors"
              title="Close Filters"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </h3>
        </div>
        <div className="p-5">
        <div className="flex flex-col sm:flex-row gap-3 items-start">
          {/* Filter Field Selector */}
          <div className="w-full sm:w-[220px] flex-shrink-0">
            <select
              value={activeFilterField}
              onChange={(e) => handleFieldChange(e.target.value)}
              className="h-10 w-full rounded-lg border border-[#d9e2f1] bg-white px-3 text-sm text-slate-700 focus:border-[#0a5fe0] focus:outline-none focus:ring-2 focus:ring-[#0a5fe0]/15"
            >
              <option value="">Select filter field</option>
              {!isReadOnlyClient && <option value="surveyor">Surveyor</option>}
              <option value="country">Country</option>
              <option value="port">Port</option>
            </select>
          </div>

          {/* Filter Options Dropdown - Shows when field is selected */}
          {activeFilterField && (
            <div className="flex-1 relative">
              <div className="rounded-lg border border-[#d9e2f1] bg-white p-3 max-h-[300px] overflow-y-auto">
                <div className="flex items-center justify-between mb-2 pb-2 border-b border-jnj-neon-200">
                  <span className="text-sm font-semibold text-jnj-neon-700">
                    Select {activeFilterField === 'surveyor' ? 'Surveyors' : activeFilterField === 'country' ? 'Countries' : 'Ports'} (Multiple)
                  </span>
                  <button
                    onClick={() => setActiveFilterField('')}
                    className="text-jnj-gray-400 hover:text-jnj-gray-600 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="space-y-1">
                  {activeFilterField === 'surveyor' && !isReadOnlyClient && availableSurveyors.length > 0 ? (
                    availableSurveyors.map(surveyor => (
                      <label key={surveyor} className="flex items-center gap-2 p-2 hover:bg-jnj-neon-50 rounded-lg cursor-pointer transition-colors">
                        <input
                          type="checkbox"
                          checked={selectedSurveyors.includes(surveyor)}
                          onChange={() => toggleOption('surveyor', surveyor)}
                          className="w-4 h-4 text-jnj-neon-600 border-jnj-gray-300 rounded focus:ring-jnj-neon-500"
                        />
                        <span className="text-sm text-jnj-gray-700">{surveyor}</span>
                      </label>
                    ))
                  ) : activeFilterField === 'surveyor' ? (
                    <p className="text-sm text-jnj-gray-500 py-2">No surveyors available</p>
                  ) : null}
                  {activeFilterField === 'country' && availableCountries.length > 0 ? (
                    availableCountries.map(country => (
                      <label key={country} className="flex items-center gap-2 p-2 hover:bg-jnj-neon-50 rounded-lg cursor-pointer transition-colors">
                        <input
                          type="checkbox"
                          checked={selectedCountries.includes(country)}
                          onChange={() => toggleOption('country', country)}
                          className="w-4 h-4 text-jnj-neon-600 border-jnj-gray-300 rounded focus:ring-jnj-neon-500"
                        />
                        <span className="text-sm text-jnj-gray-700">{country}</span>
                      </label>
                    ))
                  ) : activeFilterField === 'country' ? (
                    <p className="text-sm text-jnj-gray-500 py-2">No countries available</p>
                  ) : null}
                  {activeFilterField === 'port' && availablePorts.length > 0 ? (
                    availablePorts.map(port => (
                      <label key={port} className="flex items-center gap-2 p-2 hover:bg-jnj-neon-50 rounded-lg cursor-pointer transition-colors">
                        <input
                          type="checkbox"
                          checked={selectedPorts.includes(port)}
                          onChange={() => toggleOption('port', port)}
                          className="w-4 h-4 text-jnj-neon-600 border-jnj-gray-300 rounded focus:ring-jnj-neon-500"
                        />
                        <span className="text-sm text-jnj-gray-700">{port}</span>
                      </label>
                    ))
                  ) : activeFilterField === 'port' ? (
                    <p className="text-sm text-jnj-gray-500 py-2">No ports available</p>
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer: Selected Filters (Left) and Action Buttons (Right) */}
        <div className="mt-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-[#eef2f8]">
          {/* Selected Filters - Left side */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">Selected Filters:</span>
              <div className="flex items-center gap-2 flex-wrap">
                {selectedSurveyors.map(surveyor => (
                  <span key={surveyor} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#eef4ff] text-[#0a4fd4] ring-1 ring-[#d9e2f1] rounded-full text-xs font-medium whitespace-nowrap">
                    <i className="bi bi-person"></i> {surveyor}
                    <button
                      type="button"
                      onClick={() => toggleOption('surveyor', surveyor)}
                      className="ml-0.5 text-[#0a4fd4]/60 hover:text-[#0029a4]"
                    >
                      ×
                    </button>
                  </span>
                ))}
                {selectedCountries.map(country => (
                  <span key={country} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#eef4ff] text-[#0a4fd4] ring-1 ring-[#d9e2f1] rounded-full text-xs font-medium whitespace-nowrap">
                    <i className="bi bi-globe2"></i> {country}
                    <button
                      type="button"
                      onClick={() => toggleOption('country', country)}
                      className="ml-0.5 text-[#0a4fd4]/60 hover:text-[#0029a4]"
                    >
                      ×
                    </button>
                  </span>
                ))}
                {selectedPorts.map(port => (
                  <span key={port} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#eef4ff] text-[#0a4fd4] ring-1 ring-[#d9e2f1] rounded-full text-xs font-medium whitespace-nowrap">
                    <i className="bi bi-geo-alt"></i> {port}
                    <button
                      type="button"
                      onClick={() => toggleOption('port', port)}
                      className="ml-0.5 text-[#0a4fd4]/60 hover:text-[#0029a4]"
                    >
                      ×
                    </button>
                  </span>
                ))}
                {selectedSurveyors.length === 0 && selectedCountries.length === 0 && selectedPorts.length === 0 && (
                  <span className="text-xs text-gray-400 italic">No filters selected</span>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons - Right end */}
          <div className="flex gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={resetFilters}
              disabled={selectedSurveyors.length === 0 && selectedCountries.length === 0 && selectedPorts.length === 0}
              className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 font-semibold rounded-lg transition-colors flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Clear All
            </button>
            <button
              type="button"
              onClick={applyFilters}
              className="px-4 py-2 text-sm bg-premium text-white font-semibold rounded-lg shadow-[0_2px_8px_rgba(1,109,247,0.3)] hover:shadow-[0_4px_14px_rgba(1,109,247,0.4)] transition-shadow flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Apply Filters
            </button>
          </div>
        </div>
        </div>
      </div>
      )}
      
      {events.length === 0 && !loading && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50/70 px-4 py-3">
          <div className="flex items-start gap-2.5">
            <i className="bi bi-info-circle text-amber-600 mt-0.5"></i>
            <div className="flex-1">
              <p className="text-sm text-amber-900 font-semibold">
                {false 
                  ? 'No operations found for your client account.'
                  : 'No operations found.'}
              </p>
              <p className="text-amber-800 text-xs mt-0.5">
                {false 
                  ? 'Operations will appear here once surveys are added to the calendar for your associated client(s). If you believe this is an error, please contact your administrator to verify that your user account is properly linked to client records in the database.'
                  : 'Operations will appear here once surveys are added to the calendar.'}
              </p>
              {false && (
                <p className="text-yellow-600 text-xs mt-2 font-mono">
                  Debug: User: {user?.username}, Category: {user?.category || 'Not set'}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      
      <div className="overflow-hidden rounded-xl border border-[#d9e2f1] bg-white shadow-[0_1px_6px_rgba(1,109,247,0.12)]">
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          events={events}
          height="auto"
          timeZone="Europe/London"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,dayGridWeek',
          }}
          dayMaxEvents={3}
          moreLinkClick="popover"
          editable={!isReadOnly}
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
          eventClick={handleEventClick}
          eventContent={createEventContent(isReadOnlyClient)}
          eventDidMount={handleEventDidMount}
          eventDragStart={handleEventDragStart}
          eventDragStop={handleEventDragStop}
          eventDisplay="block"
          eventTextColor="#1f2937"
          eventBorderColor="transparent"
          eventClassNames={isReadOnly ? "cursor-pointer fc-event-read-only" : "cursor-move"}
          // Ensure smooth transitions during drag operations
          eventStartEditable={!isReadOnly}
          eventDurationEditable={!isReadOnly}
          eventMouseEnter={(info) => {
            // Only apply hover effect if not currently dragging
            // Use opacity and box-shadow instead of transform to avoid position calculation issues
            if (!isDraggingRef.current) {
              const el = info.el
              el.style.opacity = '0.95'
              el.style.transition = 'opacity 0.2s, box-shadow 0.2s'
              // Enhance box-shadow for hover effect
              const currentBoxShadow = window.getComputedStyle(el).boxShadow
              if (!currentBoxShadow || currentBoxShadow === 'none') {
                el.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)'
              }
            }
          }}
          eventMouseLeave={(info) => {
            // Reset hover effect if not currently dragging
            if (!isDraggingRef.current) {
              const el = info.el
              el.style.opacity = '1'
              el.style.boxShadow = ''
            }
          }}
        />
      </div>

      <OperationModal
        operation={selectedOperation}
        isOpen={isModalOpen}
        onClose={closeModal}
        isReadOnlyClient={isReadOnlyClient}
      />
    </div>
  )
}

export default OperationCalendar


